// src/Firebase/firebaseStorage.js
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

const storage = getStorage();

/**
 * Uploads a file to Firebase Storage under the specified folder.
 * @param {File} file - The file to upload.
 * @param {string} folder - The folder in Firebase Storage where the file will be stored.
 * @returns {Promise<{ downloadURL: string, filePath: string }>} - Returns the download URL and the file path.
 */
export const uploadFile = (file, folder) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided for upload."));
      return;
    }

    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const filePath = `${folder}/${fileName}`;
    const fileRef = ref(storage, filePath);
    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload is ${progress.toFixed(2)}% done`);
      },
      (error) => {
        console.error("Upload failed:", error);
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({ downloadURL, filePath });
        } catch (error) {
          console.error("Failed to get download URL:", error);
          reject(error);
        }
      }
    );
  });
};

/**
 * Deletes a file from Firebase Storage given its file path.
 * @param {string} filePath - The path of the file to delete in Firebase Storage.
 * @returns {Promise<void>}
 */
export const deleteFile = (filePath) => {
  return new Promise((resolve, reject) => {
    if (!filePath || typeof filePath !== "string") {
      reject(new Error("Invalid file path provided for deletion."));
      return;
    }

    const fileRef = ref(storage, filePath);
    deleteObject(fileRef)
      .then(() => {
        console.log("File deleted successfully.");
        resolve();
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        reject(error);
      });
  });
};
